import type { FunctionReturnType } from "convex/server"
import type { api } from "@/lib/api"

export type StatsOverview = FunctionReturnType<typeof api.stats.overview>
export type StatsWeek = StatsOverview["weeks"][number]
export type StatsProject = StatsOverview["projects"][number]

/** How far back the page looks, in weeks. 8 when the URL says nothing. */
export const WEEK_SPANS = [4, 8, 12] as const
export type WeekSpan = (typeof WEEK_SPANS)[number]
export const DEFAULT_WEEKS: WeekSpan = 8

/** Whose stats: the reader when the URL says nothing. */
export const TEAM = "team"

/** What the URL holds. Left out: 8 weeks, and the reader's own. */
export type StatsSearch = {
  weeks?: WeekSpan
  person?: string
}

/** The search a URL can be trusted with: anything unknown is the default. */
export function parseStatsSearch(search: Record<string, unknown>): StatsSearch {
  const weeks = WEEK_SPANS.find((span) => span !== DEFAULT_WEEKS && String(span) === String(search.weeks))
  const person =
    typeof search.person === "string" && search.person.length > 0 && search.person.length <= 128
      ? search.person
      : undefined
  return {
    ...(weeks ? { weeks } : {}),
    ...(person ? { person } : {}),
  }
}

const MINUTE_MS = 60_000
const HOUR_MS = 60 * MINUTE_MS
const DAY_MS = 24 * HOUR_MS

/** A part of a whole as a whole percent, or null when there is no whole. */
export function percentOf(part: number, whole: number): number | null {
  if (whole <= 0) return null
  return Math.round((Math.min(part, whole) / whole) * 100)
}

/**
 * How long the estimated issues took against their estimates: 1.3 is "took
 * 1.3× the estimate". Null when nothing was estimated, or when no time was
 * counted on them: an issue nobody's machine reported on did not take no
 * time, it took time nobody saw.
 */
export function estimateRatio(totals: {
  estimated: number
  estimateMinutes: number
  estimatedActualMs: number
}): number | null {
  if (totals.estimated === 0 || totals.estimateMinutes <= 0 || totals.estimatedActualMs <= 0) return null
  return totals.estimatedActualMs / (totals.estimateMinutes * MINUTE_MS)
}

/** Within a tenth either way is on the estimate: the time counted is an estimate itself. */
export function ratioKind(ratio: number): "under" | "on" | "over" {
  if (ratio < 0.9) return "under"
  if (ratio > 1.1) return "over"
  return "on"
}

/** An issue that took at least twice its estimate, or null when it had none or was not seen. */
export function overEstimate(actualMs: number, estimateMinutes: number | null): number | null {
  if (estimateMinutes === null || estimateMinutes <= 0 || actualMs <= 0) return null
  const ratio = actualMs / (estimateMinutes * MINUTE_MS)
  return ratio >= 2 ? ratio : null
}

/** An estimate, exact to the minute: it was typed, not counted. */
export function estimateParts(minutes: number): { hours: number; minutes: number } {
  const whole = Math.max(0, Math.round(minutes))
  return { hours: Math.floor(whole / 60), minutes: whole % 60 }
}

/**
 * A span of calendar time, from in progress to done: days and hours past a
 * day, hours under it, minutes under an hour. Rounded to what reads.
 */
export function spanParts(ms: number):
  | { unit: "d"; days: number; hours: number }
  | { unit: "h"; hours: number }
  | { unit: "m"; minutes: number } {
  if (ms < HOUR_MS) return { unit: "m", minutes: Math.max(1, Math.round(ms / MINUTE_MS)) }
  if (ms < DAY_MS) return { unit: "h", hours: Math.round(ms / HOUR_MS) }
  const hours = Math.round(ms / HOUR_MS)
  return { unit: "d", days: Math.floor(hours / 24), hours: hours % 24 }
}

/**
 * The top of the weekly chart: the longest week rounded up to hours that
 * read as a scale (1, 2, 5, 10, 20, 40…). At least an hour, so a quiet
 * month does not fill the chart.
 */
export function chartCeilingMs(weeks: Pick<StatsWeek, "activeMs">[]): number {
  const longest = Math.max(0, ...weeks.map((week) => week.activeMs))
  const hours = Math.max(1, Math.ceil(longest / HOUR_MS))
  const steps = [1, 2, 5, 10, 15, 20, 30, 40, 50, 60, 80, 100]
  const step = steps.find((candidate) => candidate >= hours)
  return (step ?? Math.ceil(hours / 50) * 50) * HOUR_MS
}

/** A bar's height in percent of the chart: nothing is nothing, something is seen. */
export function barPercent(value: number, ceiling: number): number {
  if (value <= 0 || ceiling <= 0) return 0
  return Math.min(100, Math.max(3, (value / ceiling) * 100))
}

/** A day of the server's ("YYYY-MM-DD", UTC) as a time to format, at noon so no zone moves it. */
export const dayAt = (day: string) => Date.parse(`${day}T12:00:00Z`)

/**
 * Which week labels a narrow chart keeps: every other one past eight weeks,
 * always the last, which is this week.
 */
export function keepsLabel(index: number, count: number): boolean {
  if (count <= 8) return true
  return (count - 1 - index) % 2 === 0
}

/** Projects by time counted, then by issues done; "no project" last among equals. */
export function sortProjects(rows: StatsProject[]): StatsProject[] {
  return [...rows].sort(
    (a, b) =>
      b.activeMs - a.activeMs ||
      b.issuesDone - a.issuesDone ||
      Number(a.project === null) - Number(b.project === null),
  )
}

/** Nothing to show at all: no time, no commits, no issue done. */
export function isEmpty(overview: Pick<StatsOverview, "totals">): boolean {
  const { activeMs, commits, issuesDone } = overview.totals
  return activeMs === 0 && commits === 0 && issuesDone === 0
}

/** The number in an identifier such as "ECH-42", for the issue's address. */
export function issueNumberOf(identifier: string): string | null {
  const match = /-([1-9]\d*)$/.exec(identifier)
  return match ? match[1] : null
}
